import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import type { ActionKind, Permission, PermissionLevel } from '@pa/core';
import { LiveMark } from '../components/ui/LiveMark';
import { PermissionMatrix } from '../components/ui/PermissionMatrix';
import { api, type PermissionsResponse, type PermissionView } from '../lib/api';

/**
 * You → What TWO can do: the permission matrix per connected account, one segmented control
 * per action class over the levels the app offers. Changed here, never from chat.
 */
export function PermissionsScreen() {
  const { t } = useTranslation();
  const [data, setData] = useState<PermissionsResponse | null>(null);
  const [failed, setFailed] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    api
      .permissions()
      .then((res) => {
        if (!cancelled) setData(res);
      })
      .catch(() => {
        if (!cancelled) setData({ connections: [], offered: {} as PermissionsResponse['offered'] });
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const change = async (view: PermissionView, action: ActionKind, level: PermissionLevel) => {
    if (!data) return;
    const previous = data;
    const patch = (list: PermissionView[], next: Permission) =>
      list.map((c) =>
        c.connection_id === view.connection_id
          ? {
              ...c,
              permissions: c.permissions.some((p) => p.action === action)
                ? c.permissions.map((p) => (p.action === action ? next : p))
                : [...c.permissions, next],
            }
          : c,
      );
    const current = view.permissions.find((p) => p.action === action);
    // Optimistic: the control moves at once; the server's row replaces it, or it rolls back.
    setData({
      ...data,
      connections: patch(data.connections, { action, level, limit: current?.limit }),
    });
    setFailed(null);
    try {
      const { permission } = await api.updatePermission({
        connection_id: view.connection_id,
        action,
        level,
      });
      setData((d) => (d ? { ...d, connections: patch(d.connections, permission) } : d));
    } catch {
      setData(previous);
      setFailed(t('permissions.saveFailed'));
    }
  };

  if (data === null) {
    return (
      <div className="flex items-center gap-3 py-4" role="status">
        <LiveMark state="thinking" size={16} title={t('common.loading')} />
      </div>
    );
  }

  const connected = data.connections.filter((c) => c.status !== 'disconnected');

  return (
    <div className="mt-4 flex flex-col gap-4">
      <p className="text-sm text-ink-2">{t('autonomy.legend')}</p>
      <details className="rounded-lg border border-line bg-surface p-3 text-sm dark:bg-elevated">
        <summary className="min-h-10 cursor-pointer list-none font-medium text-ink">
          {t('permissions.levels.summary')}
        </summary>
        <ul className="mt-2 flex flex-col gap-2 text-ink-2">
          {(['observe', 'prepare', 'ask', 'auto'] as const).map((level) => (
            <li key={level} className="flex flex-col gap-0.5">
              <span dir="auto">{t(`permissions.levels.${level}.example`)}</span>
              <span>
                <span className="font-medium text-ink">{t(`autonomy.short.${level}`)}</span>
                <span> · </span>
                <span dir="auto">{t(`permissions.levels.${level}.rule`)}</span>
              </span>
            </li>
          ))}
        </ul>
      </details>
      {failed ? (
        <p role="status" className="text-sm text-critical-text" dir="auto">
          {failed}
        </p>
      ) : null}

      {connected.length === 0 ? (
        <div
          role="status"
          className="rounded-lg border border-line bg-surface p-6 text-center text-sm text-ink-2 dark:bg-elevated"
        >
          <p>{t('settings.empty.permissions')}</p>
          <Link
            to="/you/connections"
            className="mt-2 inline-flex min-h-10 items-center text-sm font-medium text-brand"
          >
            {t('you.groups.access.title')}
          </Link>
        </div>
      ) : (
        connected.map((view) => (
          <section
            key={view.connection_id}
            aria-label={`${t(`connections.provider.${view.provider}`)} · ${view.account_email}`}
            className="rounded-lg border border-line bg-surface p-4 shadow-sm dark:bg-elevated"
          >
            <p className="text-base font-medium text-ink">
              {t(`connections.provider.${view.provider}`)}
            </p>
            <p className="text-sm text-ink-2" dir="ltr">
              {view.account_email}
            </p>
            {view.mail_available === false ? (
              <p className="mt-1 text-xs text-ink-2" dir="auto">
                {t('connections.mailUnavailable')}
              </p>
            ) : null}
            <PermissionMatrix
              view={view}
              offered={data.offered}
              onChange={(action, level) => void change(view, action, level)}
            />
          </section>
        ))
      )}
    </div>
  );
}
